import React, { useRef, forwardRef, useImperativeHandle, useEffect, useCallback, useMemo } from 'react';
import lodashThrottle from 'lodash.throttle';
import { scrollParent, checkOverflowVisible, checkNormalVisible, nextTick, cls } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
var LISTEN_FLAG = 'data-show-listened';
/**
 * 父 dom 节点集合
 * @en Parent dom node collection
 */

var wrapperNodeList = [];
/**
 * 同一父节点下监听队列
 * @en Listening queue under the same parent node
 */

var listeners = {};
var onOnceEmittedListeners = {};
var throttlingVisibleChange;
/**
 * 通过滚动事件监测 children 是否进入视口或离开视口。
 * @en Use scroll events to monitor whether children enter or leave the viewport.
 * @type 其他
 * @type_en Others
 * @name 滚动视口监听
 * @name_en ShowMonitor
 */

var ShowMonitor = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      style = props.style,
      getScrollContainer = props.getScrollContainer,
      _props$throttle = props.throttle,
      throttle = _props$throttle === void 0 ? 300 : _props$throttle,
      _props$listenResize = props.listenResize,
      listenResize = _props$listenResize === void 0 ? true : _props$listenResize,
      _props$listenScroll = props.listenScroll,
      listenScroll = _props$listenScroll === void 0 ? true : _props$listenScroll,
      _props$overflow = props.overflow,
      overflow = _props$overflow === void 0 ? false : _props$overflow,
      _props$offset = props.offset,
      offset = _props$offset === void 0 ? 0 : _props$offset,
      _props$threshold = props.threshold,
      threshold = _props$threshold === void 0 ? 0 : _props$threshold,
      _props$once = props.once,
      once = _props$once === void 0 ? false : _props$once,
      _props$children = props.children,
      children = _props$children === void 0 ? null : _props$children,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      onVisibleChange = props.onVisibleChange,
      onClick = props.onClick;
  var domRef = useRef(null);
  /**
   * 滚动容器父级元素
   * @en Scroll container parent element
   */

  var scrollPort = useRef(null);
  /**
   * 局部滚动元素父节点
   * @en Parent node of the local scroll element
   */

  var domRefParent = useRef(null);
  /**
   * 当前元素是否在可视区域内
   * @en Whether the current element is in the visible area
   */

  var isVisible = useRef(false);
  /**
   * 保存当前节点信息，类似于 class component 中 this
   * @en Save current node information, similar to this in class component
   */

  var listener = useRef(null);
  /**
   * Intersection Observer 实例化对象
   * @en Instantiated object of Intersection Observer
   */

  var io = useRef(null);
  /**
   * 父容器唯一 key（wrapperNodeList index）
   * @en The unique key of the parent container (wrapperNodeList index)
   */

  var wrapperKey = useRef(-1);
  var isChildrenExist = useRef(Boolean(children));
  var isSupportNativeApi = useMemo(function () {
    return typeof IntersectionObserver === 'function';
  }, []); // 是否支持 Intersection Observer

  useImperativeHandle(ref, function () {
    return {
      dom: domRef.current,

      /**
       * 提供检查元素是否可见的api
       * @en Provides an api to check whether the element is visible
       */
      checkVisible: function checkVisible() {
        return _checkVisible({
          node: domRef.current,
          overflow: overflow,
          onVisibleChange: onVisibleChange,
          offset: offset,
          threshold: threshold
        }, true);
      }
    };
  });

  function _checkVisible(component, ignoreCheckPreVisibleStatus) {
    if (ignoreCheckPreVisibleStatus === void 0) {
      ignoreCheckPreVisibleStatus = false;
    }

    var node = component.node,
        compOverflow = component.overflow,
        compOnce = component.once,
        onCompVisibleChange = component.onVisibleChange,
        preVisible = component.isVisible;

    if (!(node instanceof HTMLElement)) {
      return;
    }

    var parent = scrollParent(node);
    /**
     * 当前元素为非 document 元素，即为局部滚动
     * @en If the current element is a non-document element, it's local scrolling
     */

    var isOverflow = compOverflow && [node.ownerDocument, document, document.documentElement].indexOf(parent) === -1;
    /**
     * 检查元素是否可见
     * @en Check if element is visible
     */

    var curVisible = isOverflow ? checkOverflowVisible(component, parent) : checkNormalVisible(component);
    /**
     * 忽略前后可见状态判断
     * @en Ignore the visible state judgment before and after
     */

    if (ignoreCheckPreVisibleStatus) {
      return curVisible;
    }
    /**
     * 当前元素 visible 对比之前发生改变，触发回调函数
     * @en The current element visible is changed before the comparison, and the callback function is triggered
     */


    curVisible !== preVisible.current && handleCheckChildrenExist() && onCompVisibleChange(curVisible, node);
    var key = wrapperKey.current;
    /**
     * 监听一次后加入 pendingList 队列，随后被 listeners 过滤掉
     * @en After listening once, it is added to the pendingList queue, and then filtered out by listeners
     */

    if (!onOnceEmittedListeners[key]) {
      onOnceEmittedListeners[key] = [];
    }
    /**
     * 当前元素不可见 -> 可见，且 once, 触发回调函数
     * @en The current element is invisible -> visible, and once, triggers the callback function
     */


    curVisible && !preVisible.current && compOnce && onOnceEmittedListeners[key].push(component);
    preVisible.current = curVisible;
  }

  var checkVisibleHandler = useCallback(function () {
    var key = wrapperKey.current;
    var curListeners = listeners[key] || [];
    curListeners.forEach(function (listen) {
      return _checkVisible(listen);
    });
    listeners[key] = curListeners.filter(function (item) {
      return onOnceEmittedListeners[key].indexOf(item) === -1;
    });
    onOnceEmittedListeners[key] = [];
  }, []);

  function handleCheckChildrenExist() {
    return isChildrenExist.current && domRef.current && domRef.current.children.length;
  }

  function handleObserverStatusChange(entries) {
    var _io$current;

    var isIntersecting = entries[0].isIntersecting;
    /**
     * 当前元素 visible 对比之前发生改变，触发回调函数
     * @en Callback when the visible status of current element changes before the comparison
     */

    isIntersecting !== isVisible.current && handleCheckChildrenExist() && onVisibleChange(isIntersecting, domRef.current);
    /**
     * 当前元素不可见 -> 可见，且 once, 触发回调函数
     * @en The current element is invisible -> visible, and once, triggers the callback
     */

    isIntersecting && !isVisible.current && once && domRef.current && ((_io$current = io.current) == null ? void 0 : _io$current.unobserve(domRef.current));
    isVisible.current = isIntersecting;
  }
  /**
   * 非首次下disabled变化时，重新监听元素
   * @en Re-listen to the element when it is not the first time that the disabled status changes,
   */


  useEffect(function () {
    if (isSupportNativeApi) {
      // 非首次下
      // @en Not for the first time
      if (io.current && domRef.current) {
        // 禁用监听 || 监听
        disabled ? io.current.unobserve(domRef.current) : io.current.observe(domRef.current);
      }
    } else {
      // 非首次下
      // @en Not for the first time
      if (wrapperKey.current !== -1 && listener.current && listeners[wrapperKey.current]) {
        // 禁用监听，找到对应listener并删除
        // @en Disable listening, find the corresponding listener and delete it
        if (disabled) {
          var isExist = listeners[wrapperKey.current].findIndex(function (item) {
            return item.node === domRef.current;
          });

          if (isExist !== -1) {
            listeners[wrapperKey.current].splice(0, 1);
          }
        } else {
          // 开启监听
          // @en Start to listen
          listeners[wrapperKey.current].push(listener.current);
        }
      }
    }
  }, [disabled]);
  useEffect(function () {
    isChildrenExist.current = Boolean(children);
  }, [children]);
  useEffect(function () {
    scrollPort.current = getScrollContainer ? getScrollContainer() : window;
    if (!scrollPort.current) return;
    var wrapperNodeIndex = wrapperNodeList.indexOf(scrollPort.current);

    if (wrapperNodeIndex === -1) {
      // 当前滚动视口元素不在 wrapperNodeList 中, 入队列
      // @en The current scroll viewport element is not in the wrapperNodeList, push it into the list
      wrapperNodeList.push(scrollPort.current);
      wrapperKey.current = wrapperNodeList.length - 1;
    } else {
      // 取当前 index 作为 key
      // @en Take the current index as the key
      wrapperKey.current = wrapperNodeIndex;
    }

    var curWrapperKey = wrapperKey.current;

    if (isSupportNativeApi) {
      if (domRef.current) {
        domRefParent.current = scrollParent(domRef.current);
        var parent = domRefParent.current;
        var isHTMLElement = [domRef.current.ownerDocument, document, document.documentElement].indexOf(parent) === -1;
        var overflowRoot = isHTMLElement ? parent : null;
        var root = overflow ? overflowRoot : scrollPort.current === window ? null : scrollPort.current;
        var rootMargin = '';

        if (Array.isArray(offset)) {
          if (offset.length === 2) {
            rootMargin = offset[0] + "px 0px " + offset[1] + "px";
          } else {
            offset.forEach(function (val, index) {
              var space = index === 0 ? '' : ' ';
              rootMargin += "" + space + val + "px";
            });
          }
        } else {
          rootMargin = offset + "px";
        }

        io.current = new IntersectionObserver(handleObserverStatusChange, {
          root: root,
          rootMargin: rootMargin,
          threshold: threshold
        });
        !disabled && io.current.observe(domRef.current);
      }
    } else {
      // 节流后回调函数
      // @en Callback after throttling
      throttlingVisibleChange = checkVisibleHandler;
      throttle && (throttlingVisibleChange = lodashThrottle(throttlingVisibleChange, throttle));

      if (overflow) {
        if (domRef.current) {
          domRefParent.current = scrollParent(domRef.current);
          var _parent = domRefParent.current;

          if (_parent && _parent instanceof HTMLElement && typeof _parent.getAttribute === 'function') {
            var listenerCount = 1 + Number(_parent.getAttribute(LISTEN_FLAG));

            if (!listeners[curWrapperKey]) {
              listeners[curWrapperKey] = [];
            }

            if (listenerCount === 1) {
              // listener 数量为1时监听事件（列表中插入 ShowMonitor 时做判断）
              // @en Listen when the number of listeners is 1 (judging when ShowMonitor is inserted into the list)
              _parent.addEventListener('scroll', throttlingVisibleChange);
            }

            _parent.setAttribute(LISTEN_FLAG, String(listenerCount));
          }
        }
      } else if (!listeners[curWrapperKey] || listeners[curWrapperKey].length === 0 || // 相同curWrapperKey内全部为overflow场景，需要单独注册scroll事件
      // @en It need to register the scroll event separately when all values in the same curWrapperKey are overflow
      listeners[curWrapperKey] && listeners[curWrapperKey].every(function (it) {
        return it.overflow;
      })) {
        if (!listeners[curWrapperKey]) {
          listeners[curWrapperKey] = [];
        }

        listenScroll && scrollPort.current.addEventListener('scroll', throttlingVisibleChange);
        listenResize && scrollPort.current.addEventListener('resize', throttlingVisibleChange);
      }

      listener.current = {
        node: domRef.current,
        isVisible: isVisible,
        overflow: overflow,
        once: once,
        offset: offset,
        threshold: threshold,
        onVisibleChange: onVisibleChange
      }; // 将当前 listener 存入 listeners

      !disabled && listeners[curWrapperKey].push(listener.current);
      nextTick(function () {
        !disabled && _checkVisible(listener.current);
      });
    }
  }, []);
  useEffect(function () {
    return function () {
      if (isSupportNativeApi) {
        var _io$current2;

        domRef.current && ((_io$current2 = io.current) == null ? void 0 : _io$current2.unobserve(domRef.current));
      } else {
        if (overflow) {
          var parent = domRefParent.current;

          if (parent && parent instanceof HTMLElement && typeof parent.getAttribute === 'function') {
            var listenerCount = Number(parent.getAttribute(LISTEN_FLAG) || 0) - 1;

            if (listenerCount === 0) {
              // 如果监听队列中无节点，解绑事件
              // @en If there is no node in the listening queue, unbind the event
              parent.removeEventListener('scroll', throttlingVisibleChange);
            } else {
              // 如果监听队列中有元素节点，将 dataset 减1
              // @en If there are element nodes in the listening queue, decrement dataset by 1
              parent.setAttribute(LISTEN_FLAG, String(listenerCount));
            }
          }
        }

        var _key = wrapperKey.current;
        var curListeners = listeners[_key];

        if (curListeners) {
          // 不在监听队列中，移除
          // @en Not in the listening queue, remove it
          var index = curListeners.indexOf(listener.current);
          index !== -1 && curListeners.splice(index, 1); // 监听队列为空，解绑 scroll、resize 事件
          // @en When the listening queue is empty, unbind scroll and resize events

          if (curListeners.length === 0) {
            delete listeners[_key];

            if (scrollPort.current) {
              scrollPort.current.removeEventListener('scroll', throttlingVisibleChange);
              scrollPort.current.removeEventListener('resize', throttlingVisibleChange);
            }
          }
        }
      }
    };
  }, []);
  return /*#__PURE__*/React.createElement(ContextLayout, null, function (_ref) {
    var prefixCls = _ref.prefixCls;
    return /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-show-monitor", className),
      style: style,
      ref: domRef,
      onClick: onClick
    }, children);
  });
});
export default ShowMonitor;